import React from 'react';
import { Platform, StyleSheet, View, ViewStyle } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../../context/ThemeContext';

/**
 * CORE-974 parity for native.
 *
 * On web every solid-orange action is upgraded to the approved warm gradient by
 * a CSS compatibility layer (`public/global.css`, "legacy orange actions"):
 * `--gradient-cta` + pill radius + soft shadow. Native has no stylesheet, so
 * each button has to opt in — drop this fill in as the first child of the
 * existing touchable and swap its `backgroundColor` for `ctaSurface`.
 *
 *   <TouchableOpacity style={[styles.submit, ctaSurface]}>
 *     <CtaGradientFill />
 *     <Text style={styles.submitLabel}>…</Text>
 *   </TouchableOpacity>
 *
 * The fill is absolutely positioned and non-interactive, so it never changes
 * the button's layout or its press handling.
 */
export const CtaGradientFill: React.FC<{ radius?: number }> = ({ radius = 999 }) => {
  const { colors } = useTheme();
  return (
    <LinearGradient
      // Deliberately a wider ramp than the web's flat 90° `--gradient-cta`:
      // on a phone the button is narrow and often sits on a dark photo, where
      // #F45122 → #FF955D across a short horizontal run reads as one flat
      // orange. A diagonal sweep with a deeper first stop keeps the same warm
      // family (all three are CTA palette tokens) and stays legible at 65%
      // opacity, which is what the disabled state renders at.
      colors={[colors.ctaPressedStart, colors.ctaStart, colors.ctaEnd]}
      locations={[0, 0.45, 1]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={[StyleSheet.absoluteFill, { borderRadius: radius }]}
      pointerEvents="none"
    />
  );
};

/**
 * Surface for a button carrying `CtaGradientFill`: clipped, so the fill follows
 * the button's radius. The flat colour underneath is the gradient's first stop
 * (`ctaStart`, identical in both themes) — invisible under the fill, but it
 * keeps Android's elevation shadow alive, which a transparent background kills.
 */
export const ctaSurface: ViewStyle = {
  backgroundColor: '#F45122',
  overflow: 'hidden',
};

/**
 * Градиентный БОРДЕР для чипов/пилюль: пилюля-градиент с padding в толщину
 * рамки, внутри — вью цвета подложки. RN не умеет border-image, поэтому рамка
 * фейкуется двумя слоями; `innerColor` обязан совпадать с фоном, на котором
 * лежит чип, иначе «рамка» превратится в заливку.
 */
export const CtaGradientBorder: React.FC<{
  radius?: number;
  width?: number;
  innerColor: string;
  style?: ViewStyle | ViewStyle[];
  innerStyle?: ViewStyle | ViewStyle[];
  children?: React.ReactNode;
}> = ({ radius = 999, width = 1.5, innerColor, style, innerStyle, children }) => {
  const { colors } = useTheme();
  return (
    <LinearGradient
      colors={[colors.ctaPressedStart, colors.ctaStart, colors.ctaEnd]}
      locations={[0, 0.45, 1]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={[{ borderRadius: radius, padding: width }, style as any]}
    >
      <View style={[{ borderRadius: Math.max(0, radius - width), backgroundColor: innerColor }, innerStyle as any]}>
        {children}
      </View>
    </LinearGradient>
  );
};

/**
 * Native counterpart of `--shadow-cta-soft`. Web gets it from `ctaShadow`
 * (a CSS box-shadow string RN can't parse); these are the same warm glow
 * expressed with iOS shadow props + Android elevation.
 */
export const ctaShadow: ViewStyle = Platform.select({
  ios: {
    shadowColor: '#B8532A',
    shadowOpacity: 0.26,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 8 },
  },
  android: { elevation: 6 },
  default: {},
}) as ViewStyle;
